import { FC, useEffect, useState } from 'react';
import { Outlet } from '@tanstack/react-router';
import AppSidebar from './components/app-sidebar';
import { Box, Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import { useDocumentTitle } from 'usehooks-ts';
import AppHeader from './components/app-header';
import useApp from './hooks/use-app.hook';
import ModalRoot from './contexts/modal/modal-root';
import Text from './components/text';
import { useIsFetching, useQueryClient } from 'react-query';
import useModal from './hooks/use-modal.hook';
import { AxiosError } from 'axios';
import usePrior from './hooks/use-prior.hook';
import { DrawerProvider } from './contexts/drawer';
import { UserControlProvider } from './contexts/user-control';
import DrawerRoot from './contexts/drawer/drawer-root';

const SIDEBAR_WIDTH = 240;

const useStyles = createUseStyles<string, { signedIn: boolean }>((theme: Theme) => ({
  main: {
    flexGrow: 1,
    overflow: 'hidden',
    backgroundColor: ({ signedIn }) =>
      signedIn ? theme.palette.background.app : theme.palette.common.white,
  },
  mainScroll: {
    width: '100%',
    flexGrow: 1,
    overflowY: 'auto',
    scrollbarGutter: 'stable',
    paddingTop: 24,
  },
  mainContent: ({ signedIn }) => ({
    width: '100%',
    height: '100%',
    minHeight: signedIn ? '100%' : 'inherit',
    padding: '0px 24px',
    boxSizing: 'border-box',
    margin: '0 auto',
  }),
  '@global': {
    body: {
      height: '100vh',
      width: '100vw',
      padding: 0,
      margin: 0,
      overflowY: 'hidden',
    },
    'div#root': {
      height: '100%',
      width: '100%',
      padding: 0,
      margin: 0,
      overflowY: 'hidden',
    },
    input: {
      '&:-webkit-autofill': {
        background: 'none',
        backgroundColor: 'transparent !important',
        backgroundClip: 'text',
      },
    },
    a: {
      color: theme.palette.link.main,
      textDecoration: 'none',
      '&:hover': {
        color: theme.palette.link.hover,
      },
      '&.flat': {
        color: theme.palette.common.black,
      },
    },
    '& .notistack-SnackbarContainer': {
      '& .notistack-MuiContent.notistack-MuiContent-info': {
        backgroundColor: theme.palette.info.main,
      },
      '& .notistack-MuiContent.notistack-MuiContent-warning': {
        backgroundColor: theme.palette.warning.main,
      },
    },
  },
}));

const App: FC = () => {
  const { openModal } = useModal();
  const { signedIn, pageTitle, setPageLoadState } = useApp();
  const { plain } = pageTitle;
  const hasPageTitle = Boolean(plain);
  const styles = useStyles({ signedIn });

  useDocumentTitle(`F5 Workforce AI Security ${hasPageTitle ? '| ' : ''} ${plain}`);

  const queryClient = useQueryClient();
  const isFetching = useIsFetching();
  const priorIsFetching = usePrior(isFetching);

  useEffect(() => {
    const queryCache = queryClient.getQueryCache();
    const unsubscribe = queryCache.subscribe(() => {
      const errorQuery = queryCache.getAll().find((query) => query.state.error);
      if (errorQuery) {
        let error = 'There was a network error';
        try {
          if ((errorQuery.state.error as AxiosError).response?.status === 401) {
            error = 'Please reload the page';
          }
        } catch {
          console.error('unexpected query error', errorQuery);
        }

        openModal('app-error', { error });
        unsubscribe();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [queryClient, openModal]);

  useEffect(() => {
    if (isFetching && !priorIsFetching) {
      setPageLoadState('loading');
      return;
    }

    if (!isFetching && priorIsFetching) {
      setTimeout(() => {
        setPageLoadState('unloaded');
      }, 300);
      return;
    }
  }, [isFetching, priorIsFetching, setPageLoadState]);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const handleSidebarToggleOpen = () => {
    setSidebarOpen(!sidebarOpen);
  };
  return (
    <UserControlProvider>
      <DrawerProvider>
        <Stack height="100%" width="100%" overflow="hidden" minHeight="0px" minWidth="0px">
          <Stack direction="row" flexGrow={1} minHeight="0px" minWidth="0px">
            {signedIn && (
              <AppSidebar
                width={SIDEBAR_WIDTH}
                open={sidebarOpen}
                onToggleOpen={handleSidebarToggleOpen}
              />
            )}
            <Stack
              component="main"
              className={styles.main}
              alignItems={signedIn ? 'flex-start' : 'center'}
            >
              <AppHeader signedIn={signedIn} />
              <Box className={styles.mainScroll}>
                <Box className={styles.mainContent} maxWidth="xl">
                  {!signedIn && hasPageTitle && (
                    <Stack alignItems="center">
                      <Text size="x-large" noWrap>
                        {plain}
                      </Text>
                    </Stack>
                  )}
                  <Outlet />
                </Box>
              </Box>
            </Stack>
          </Stack>
        </Stack>
        <ModalRoot />
        <DrawerRoot />
      </DrawerProvider>
    </UserControlProvider>
  );
};

export default App;
